import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import Button from 'components/Button';

import css from './TableBlock.module.scss';

import OnPageEditing from 'components/OnPageEditing';

import { THEMES, getThemeClassNames } from 'constants/themes';
const getRow = (data) => (data && typeof data === 'object') ? Object.keys(data).sort() : [];
const getHeaderColumns = (data, keys) => keys.map((key, i) => typeof data[key] === "string" ? (<th key={i} dangerouslySetInnerHTML={{__html:data[key]}}></th>): <th key={i}>{data[key]}</th>);
const getColumns = (data, keys) => keys.map((key, i) => typeof data[key] === "string" ? (<td key={i} dangerouslySetInnerHTML={{__html:data[key]}}></td>): <td key={i}>{data[key]}</td>);

class TableBlock extends React.Component {
    static propTypes = {
        heading: PropTypes.string,
        text: PropTypes.string,
        data: PropTypes.array.isRequired,
        theme: PropTypes.oneOf(Object.keys(THEMES).map(s => THEMES[s])),
        readMoreText: PropTypes.string,
        readMoreLink: PropTypes.string,
        keys: PropTypes.array,
        rightAlign: PropTypes.bool.isRequired,
    };

    static defaultProps = {
        theme: 0,
        rightAlign: false,
    };

    state = {
        hasOverflowScroll: false,
        hadScrollInteraction: false
    };

    constructor(props) {
        super(props);

        this.rows = props.data && this._filterArray(props.data);
    }

    componentDidMount() {
        this.checkOverflowScroll();

        this.tableContainer.addEventListener("scroll", this.handleScroll);
        (typeof window !== 'undefined') && window.addEventListener("resize", this.handleResize);
    }

    componentWillReceiveProps(props) {
        if(props.data !== this.props.data) {
            this.rows = this._filterArray(props.data);
        }
    }

    componentWillUnmount() {
        this.tableContainer.removeEventListener("scroll", this.handleScroll);
        (typeof window !== 'undefined') && window.removeEventListener("resize", this.handleResize);
    }

    handleResize = throttle(() => {
        this.checkOverflowScroll();
    }, 500)

    handleScroll = () => {
        if (this.tableContainer.scrollLeft > 0) {
            this.setState({ hadScrollInteraction: true });
        }
    }

    checkOverflowScroll = () => {
        if(! this.tableContainer) return;

        this.setState({
            hasOverflowScroll: this.tableContainer.scrollWidth > this.tableContainer.offsetWidth
        });
    }

    _filterArray(data) {
        if(! Array.isArray(data[0])) return data;
        if(data.length === 0) return data;

        // When we get a multidimensional array it will
        // contain 10 cells per row which might contain
        // empty cells so we need to find last cell which
        // has a value so we can filter out all extraneous
        // empty cells

        let foundIndex = -1;
        let rows = [];

        for(let i = (data[0].length-1); i >= 0; i--) {
            for(let x = 0; x < data.length; x++) {
                if(data[x][i]) {
                    foundIndex = i;
                    break;
                }
            }

            if(foundIndex !== -1) {
                rows = data.map(row => row.filter((cell, index) => index <= foundIndex));
                break;
            }
        }

        return rows;
    }

    render() {
        const {
            heading,
            text,
            theme,
            readMoreLink,
            readMoreText,
            keys,
            rightAlign,
        } = this.props;

        return (
            <div
                className={cn(css.block, getThemeClassNames(css, theme))}
                data-block-type="TableBlock"
            >
                <div className={cn(css.content, {
                    [css["has-overflow"]]: this.state.hasOverflowScroll,
                    [css["had-interaction"]]: this.state.hadScrollInteraction
                })}>
                    {heading && (
                        <OnPageEditing property="Heading">
                            <h2
                                className={cn(css.heading, "block-title-large")}
                                dangerouslySetInnerHTML={{__html: heading }}
                            />
                        </OnPageEditing>
                    )}
                    
                    <div
                        ref={d => this.tableContainer = d}
                        className={css["table-swipe"]}
                    >
                        <div className={css["swipe-hint"]}>
                            <div className={css["swipe-hint-hand"]} />
                            <div className={css["swipe-hint-trail"]} />
                        </div>
                        <OnPageEditing property="Data">
                            <table className={cn({ [css.rightAlign]: rightAlign })}>
                                <thead>
                                    {this.rows && [this.rows[0]].map((v, i) => (
                                        <tr key={i}>{getHeaderColumns(v, keys || getRow(v))}</tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {this.rows && this.rows.filter((o, i) => i > 0).map((v, i) => (
                                        <tr key={i}>{getColumns(v, keys || getRow(v))}</tr>
                                    ))}
                                </tbody>
                            </table>
                        </OnPageEditing>
                    </div>
                    {text && (
                        <OnPageEditing property="Text">
                            <p className={css["table-text"]}>{text}</p>
                        </OnPageEditing>
                    )}
                    {readMoreText && readMoreLink && (
                        <p className={css.readMore}>
                            <Button secondary href={readMoreLink}>{readMoreText}</Button>
                        </p>
                    )}
                </div>
            </div>
        );
    }
}

export default TableBlock;



// WEBPACK FOOTER //
// ./src/components/TableBlock/TableBlock.jsx